import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { lotsFromDoc, balanceOf, compactLots, removeNewestFirst } from '../utils/loyalty';
import toast from 'react-hot-toast';

const STATUS_FLOW  = ['Preparing','Ready','Delivered'];
const STATUS_COLOR = { Preparing:'tag-red', Ready:'tag-amber', Delivered:'tag-green' };
const CH_COLOR     = { dinein:'tag-blue', zomato:'tag-red', swiggy:'tag-amber', whatsapp:'tag-green' };

export default function Orders() {
  const { role } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null); // the bill being considered for deletion

  useEffect(()=>{
    const q=query(collection(db,'bills'),orderBy('createdAt','desc'));
    return onSnapshot(q,s=>setOrders(s.docs.map(d=>({id:d.id,...d.data()}))));
  },[]);

  const advance = async (id, cur) => {
    const next = STATUS_FLOW[STATUS_FLOW.indexOf(cur) + 1];
    if (!next) return;
    await updateDoc(doc(db, 'bills', id), { status: next, updatedAt: serverTimestamp() });
    toast.success('Marked: ' + next);

  };

  // ── DELETE BILL (admin only, with loyalty-reversal choice) ──
  const performDelete = async (bill, reverseLoyalty) => {
    try {
      if (reverseLoyalty && bill.customerPhone) {
        const lRef = doc(db,'loyalty', bill.customerPhone);
        const lSnap = await getDoc(lRef);
        if (lSnap.exists()) {
          const earned   = bill.pointsEarned ?? Math.floor(bill.subtotal || 0);   // points this bill added
          const redeemed = bill.pointsRedeemed || 0;                               // points this bill spent
          // take the earned points back off the newest points, give the redeemed points back as fresh points
          let lots = removeNewestFirst(lotsFromDoc(lSnap.data()), earned);
          if (redeemed > 0) lots = [...lots, { pts: redeemed, at: Date.now() }];
          lots = compactLots(lots);
          await setDoc(lRef, { points: balanceOf(lots), lots }, { merge: true });
        }
      }
      await deleteDoc(doc(db,'bills', bill.id));
      toast.success(`Bill ${bill.billNo || bill.id.slice(0,6)} deleted`);
    } catch (err) { toast.error(err.message); }
    setDeleteTarget(null);
  };

  const filtered=filter==='all'?orders:orders.filter(o=>o.channel===filter||o.status===filter);

  return (
    <div>
      <div style={{ display:'flex', gap:7, marginBottom:14, flexWrap:'wrap' }}>
        {['all','dinein','zomato','swiggy','whatsapp','Preparing','Ready','Delivered'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{
            padding:'5px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
            borderColor:filter===f?'#D85A30':'#ddd', background:filter===f?'#FAECE7':'#fff',
            color:filter===f?'#993C1D':'#555', textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>
      {filtered.length===0 && <div style={{ textAlign:'center', padding:40, color:'#bbb' }}>No orders</div>}
      {filtered.map(o=>(
        <div key={o.id} className="card" style={{ marginBottom:9, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px' }}>
          <div>
            <div style={{ fontWeight:500, fontSize:13, marginBottom:3 }}>
              {o.billNo ? o.billNo+' — ' : ''}{o.orderLabel || o.table || '—'} <span className={`tag ${CH_COLOR[o.channel]||'tag-gray'}`} style={{ marginLeft:7, textTransform:'capitalize' }}>{o.channel}</span>
            </div>
            <div style={{ fontSize:12, color:'#888' }}>{(o.items||[]).map(i=>i.name).join(', ')}</div>
            <div style={{ fontSize:11, color:'#bbb', marginTop:2 }}>{o.createdAt?.toDate?.()?.toLocaleTimeString?.()||'Just now'}</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
            <div style={{ fontWeight:600 }}>₹{o.total}</div>
            <span className={`tag ${STATUS_COLOR[o.status||'Preparing']}`}>{o.status||'Preparing'}</span>
            {STATUS_FLOW.indexOf(o.status||'Preparing')<STATUS_FLOW.length-1 && (
              <button className="btn" style={{ fontSize:11, padding:'3px 9px' }}
                onClick={()=>advance(o.id,o.status||'Preparing')}>
                → {STATUS_FLOW[STATUS_FLOW.indexOf(o.status||'Preparing')+1]}
              </button>
            )}
            {role === 'admin' && (
              <button className="btn btn-danger" style={{ fontSize:11, padding:'3px 9px' }} onClick={()=>setDeleteTarget(o)}>
                🗑 Delete
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Step 1: confirm delete */}
      {deleteTarget && deleteTarget._confirming !== false && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:400 }}>
            <h3>⚠️ Delete Bill {deleteTarget.billNo || deleteTarget.id.slice(0,6)}?</h3>
            <p style={{ fontSize:13, color:'#666', marginBottom:14 }}>
              This permanently removes the bill (₹{deleteTarget.total}) from your records. This cannot be undone.
            </p>
            {deleteTarget.customerPhone ? (
              <div style={{ background:'#FAEEDA', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#854F0B', marginBottom:14 }}>
                This customer earned <b>{deleteTarget.pointsEarned ?? Math.floor(deleteTarget.subtotal||0)} pts</b> and redeemed <b>{deleteTarget.pointsRedeemed||0} pts</b> on this bill.
                Do you also want to reverse those loyalty points?
              </div>
            ) : (
              <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>No customer was attached to this bill — nothing to reverse.</div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {deleteTarget.customerPhone && (
                <button className="btn btn-danger" onClick={()=>performDelete(deleteTarget, true)}>
                  🗑 Delete Bill AND Reverse Loyalty Points
                </button>
              )}
              <button className="btn btn-danger" style={{ background:'#e2777a' }} onClick={()=>performDelete(deleteTarget, false)}>
                🗑 Delete Bill Only (keep points as-is)
              </button>
              <button className="btn" onClick={()=>setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}